import React, { useState, useRef, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar as CalendarIcon, X, Upload, Eye, Download, PlusIcon, FileText, ChevronDown } from 'lucide-react';
import { AppSelect } from '../../components/common/select';
import './AuditSchedule.css';

const ACCEPTED_DOC_TYPES = '.pdf,.docx,.jpg,.jpeg';

const OFFICIAL_TRIP_PURPOSES = [
  { id: 'transport-flight-ticket', name: 'Transport Flight Ticket' },
  { id: 'taxi-online', name: 'Taxi Online' },
  { id: 'hotels-official-trip', name: 'Hotels for official trip' },
];

const COMPANY_FLAG_LABELS = {
  priority: 'Priority',
  exclusive: 'Exclusive',
  engaged: 'Engaged',
  cbqa_malaysia: 'CBQA Malaysia',
  i_marks: 'i-Marks',
  grg: 'GRG',
  egg: 'EGG',
  new_client: 'New Client',
  disqualified: 'Disqualified',
  binary_work_client: 'Binary Work Client',
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AuditSchedule = () => {
  const { projectId } = useParams();
  const fileInputRef = useRef(null);
  const [startDate, setStartDate] = useState('2025-08-31');
  const [endDate, setEndDate] = useState('2025-08-31');
  const [activeTab, setActiveTab] = useState('project-details');
  const [auditType, setAuditType] = useState('surveillance-1');
  const [lastActivityDate, setLastActivityDate] = useState('2025-09-02');
  const [latestAuditDate, setLatestAuditDate] = useState('2025-09-02');
  const [totalEmployee, setTotalEmployee] = useState(2);
  const [employees, setEmployees] = useState([
    {
      id: 1,
      startDate: '2025-09-04',
      endDate: '2025-09-06',
      employee: 'James Thompson',
      role: 'GHG Expert',
      mandays: 6,
      auditType: 'Onsite',
      scheduleStatus: 'Booked',
      site: 'Site 1',
    },
    {
      id: 2,
      startDate: '2025-09-04',
      endDate: '2025-09-06',
      employee: 'Roger David',
      role: 'GHG Expert',
      mandays: 6,
      auditType: 'Onsite',
      scheduleStatus: 'Booked',
      site: 'Site 2',
    },
  ]);
  const [documents, setDocuments] = useState([
    { id: 1, name: 'document_support_1.pdf', size: '2MB' },
    { id: 2, name: 'document_support_2.pdf', size: '2MB' },
  ]);

  // Surveillance/service item from API – set when loading audit schedule or detail project
  const [surveillanceItem, setSurveillanceItem] = useState({ name: 'Surveillance - 1' });

  // Site Location tab
  const [needTransportation, setNeedTransportation] = useState('yes');
  const [needAccommodation, setNeedAccommodation] = useState('yes');
  const [sites] = useState([
    {
      id: 1,
      country: 'Indonesia',
      city: 'DKI Jakarta',
      siteAddress: 'Gd. Merdeka, Jl. Pahlawan No.08 Jakarta 122101',
      contactPerson: 'John Doe',
      type: 'Customer/ Client',
      email: 'client@gmail.com',
      phone: '081212121288',
      person: { initials: 'JT', name: 'James Thompson', mandays: 6, auditType: 'Onsite' },
    },
    {
      id: 2,
      country: 'Indonesia',
      city: 'DKI Jakarta',
      siteAddress: 'Gd. Merdeka, Jl. Pahlawan No.08 Jakarta 122101',
      contactPerson: 'John Doe',
      type: 'Customer/ Client',
      email: 'client@gmail.com',
      phone: '081212121288',
      person: { initials: 'RD', name: 'Roger David', mandays: 6, auditType: 'Onsite' },
    },
  ]);

  // Official Trip tab: accommodations per site
  const createAccommodation = () => ({
    id: Date.now() + Math.random(),
    purpose: 'transport-flight-ticket',
    auditStartDate: '2025-09-04',
    auditEndDate: '2025-09-06',
    duration: 1,
    amount: '',
    needPickupDropOff: false,
    contacts: [{ id: Date.now() + Math.random(), name: '', phone: '', note: '' }],
    documents: [],
    providedByClient: false,
  });

  const [officialTripAccommodations, setOfficialTripAccommodations] = useState(() => {
    const acc1 = createAccommodation();
    acc1.amount = 'Via Go Corp / Grab Corporate';
    acc1.documents = [{ id: 1, name: 'rincian_biaya.pdf', size: '2MB' }];
    const acc2 = createAccommodation();
    acc2.purpose = 'hotels-official-trip';
    acc2.amount = 'Hotel Mulia, Check in 15 December 2025 Check Out 18 December 2025';
    acc2.documents = [{ id: 2, name: 'rincian_biaya.pdf', size: '2MB' }];
    acc2.providedByClient = true;
    const acc3 = createAccommodation();
    acc3.amount = 'To be provided and issued by Client 15 December 2025 Soekarno Hatta International Airport (CGK) - Airport Tarakan Juwata (TRK) 10.20 - 13.55 (Batik Air)';
    acc3.needPickupDropOff = true;
    acc3.auditEndDate = '2025-09-06';
    acc3.documents = [{ id: 3, name: 'rincian_biaya.pdf', size: '2MB' }];
    return {
      1: [acc1, acc2],
      2: [acc3],
    };
  });
  const [officialTripPersonExpanded, setOfficialTripPersonExpanded] = useState({ 'James Thompson': true, 'Roger David': true });
  const [officialTripSiteExpanded, setOfficialTripSiteExpanded] = useState({ 1: true, 2: true });
  const officialTripFileInputRef = useRef(null);

  const sitesByPerson = useMemo(() => {
    const map = {};
    sites.forEach((site) => {
      const name = site.person.name;
      if (!map[name]) map[name] = { personName: name, personInitials: site.person.initials, sites: [] };
      map[name].sites.push(site);
    });
    return Object.values(map);
  }, [sites]);
  const [officialTripUploadTarget, setOfficialTripUploadTarget] = useState(null);

  const company = {
    initials: 'GT',
    name: 'PT Global Indo Tech',
    id: '000125',
    flagging: 'priority', // priority | exclusive | engaged | cbqa_malaysia | i_marks | grg | egg | new_client | disqualified | binary_work_client
    email: 'company@mail.com',
    sector: 'Financial Intermediation',
    phone: '021 5353535 512',
    companySize: '11-50',
    website: 'www.company.com',
    address: 'Gd. Merdeka, Jl. Pahlawan No.08 DKI Jakarta, 122101',
  };

  const addEmployee = () => {
    setEmployees((prev) => [
      ...prev,
      {
        id: Date.now(),
        startDate: '',
        endDate: '',
        employee: '',
        role: '',
        mandays: '',
        auditType: '',
        scheduleStatus: '',
        site: '',
      },
    ]);
  };

  const removeEmployee = (id) => {
    setEmployees((prev) => prev.filter((e) => e.id !== id));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const newDocs = Array.from(files).map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: formatFileSize(file.size),
      file,
    }));
    setDocuments((prev) => [...prev, ...newDocs]);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('audit-schedule-upload-zone--dragover');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('audit-schedule-upload-zone--dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('audit-schedule-upload-zone--dragover');
    const files = e.dataTransfer.files;
    if (!files?.length) return;
    const newDocs = Array.from(files).map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: formatFileSize(file.size),
      file,
    }));
    setDocuments((prev) => [...prev, ...newDocs]);
  };

  const removeDocument = (id) => {
    setDocuments((prev) => prev.filter((d) => d.id !== id));
  };

  // Official Trip handlers
  const toggleOfficialTripPersonSection = (personName) => {
    setOfficialTripPersonExpanded((prev) => ({ ...prev, [personName]: !prev[personName] }));
  };
  const toggleOfficialTripSiteSection = (siteId) => {
    setOfficialTripSiteExpanded((prev) => ({ ...prev, [siteId]: !prev[siteId] }));
  };

  const addOfficialTripAccommodation = (siteId) => {
    setOfficialTripAccommodations((prev) => ({
      ...prev,
      [siteId]: [...(prev[siteId] || []), createAccommodation()],
    }));
  };

  const removeOfficialTripAccommodation = (siteId, accId) => {
    setOfficialTripAccommodations((prev) => ({
      ...prev,
      [siteId]: (prev[siteId] || []).filter((a) => a.id !== accId),
    }));
  };

  const updateOfficialTripAccommodation = (siteId, accId, field, value) => {
    setOfficialTripAccommodations((prev) => ({
      ...prev,
      [siteId]: (prev[siteId] || []).map((a) =>
        a.id === accId ? { ...a, [field]: value } : a
      ),
    }));
  };

  const addOfficialTripContact = (siteId, accId) => {
    setOfficialTripAccommodations((prev) => ({
      ...prev,
      [siteId]: (prev[siteId] || []).map((a) =>
        a.id === accId
          ? { ...a, contacts: [...a.contacts, { id: Date.now() + Math.random(), name: '', phone: '', note: '' }] }
          : a
      ),
    }));
  };

  const updateOfficialTripContact = (siteId, accId, contactId, field, value) => {
    setOfficialTripAccommodations((prev) => ({
      ...prev,
      [siteId]: (prev[siteId] || []).map((a) =>
        a.id === accId
          ? { ...a, contacts: a.contacts.map((c) => (c.id === contactId ? { ...c, [field]: value } : c)) }
          : a
      ),
    }));
  };

  const removeOfficialTripContact = (siteId, accId, contactId) => {
    setOfficialTripAccommodations((prev) => ({
      ...prev,
      [siteId]: (prev[siteId] || []).map((a) =>
        a.id === accId ? { ...a, contacts: a.contacts.filter((c) => c.id !== contactId) } : a
      ),
    }));
  };

  const openOfficialTripUpload = (siteId, accId) => {
    setOfficialTripUploadTarget({ siteId, accId });
    officialTripFileInputRef.current?.click();
  };

  const handleOfficialTripFileChange = (e) => {
    const files = e.target.files;
    if (!files?.length || !officialTripUploadTarget) return;
    const { siteId, accId } = officialTripUploadTarget;
    const newDocs = Array.from(files).map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: formatFileSize(file.size),
      file,
    }));
    setOfficialTripAccommodations((prev) => ({
      ...prev,
      [siteId]: (prev[siteId] || []).map((a) =>
        a.id === accId ? { ...a, documents: [...a.documents, ...newDocs] } : a
      ),
    }));
    setOfficialTripUploadTarget(null);
    e.target.value = '';
  };

  const removeOfficialTripDocument = (siteId, accId, docId) => {
    setOfficialTripAccommodations((prev) => ({
      ...prev,
      [siteId]: (prev[siteId] || []).map((a) =>
        a.id === accId ? { ...a, documents: a.documents.filter((d) => d.id !== docId) } : a
      ),
    }));
  };

  return (
    <div className="audit-schedule-page">
      <nav aria-label="breadcrumb" className="audit-schedule-breadcrumb">
        <ol className="breadcrumb mb-1">
          <li className="breadcrumb-item">
            <Link to="/calendar">Calendar</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Audit Schedule
          </li>
        </ol>
      </nav>
      <h1 className="audit-schedule-title">Form Audit Schedule</h1>

      <div className="audit-schedule-dates row g-2 mb-4">
        <div className="col-md-6">
          <label className="form-label">Start Date</label>
          <div className="input-group">
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <span className="input-group-text">
              <CalendarIcon size={18} />
            </span>
          </div>
        </div>
        <div className="col-md-6">
          <label className="form-label">End Date</label>
          <div className="input-group">
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <span className="input-group-text">
              <CalendarIcon size={18} />
            </span>
          </div>
        </div>
      </div>

      {/* Company / Project card */}
      <div className="card audit-schedule-company-card mb-4">
        <div className="audit-schedule-company-banner" />
        <div className="audit-schedule-company-card-body">
          <div className="d-flex flex-column align-items-start gap-2">
            <span className="audit-schedule-company-avatar">{company.initials}</span>
            <div className="w-100">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <h2 className="audit-schedule-company-name mb-0">{company.name}</h2>
                {company.flagging && (
                <span
                  className={`audit-schedule-light-badge audit-schedule-light-badge--${
                    COMPANY_FLAG_LABELS[company.flagging] ? company.flagging : 'default'
                  }`}
                >
                  {COMPANY_FLAG_LABELS[company.flagging] ?? company.flagging}
                </span>
              )}
              </div>
              <span className="audit-schedule-company-id">{company.id}</span>
            </div>
          </div>
          <div className="card audit-schedule-company-details-card mt-3">
            <div className="card-body">
              <div className="audit-schedule-company-grid">
                <div>
                  <span className="audit-schedule-detail-label">Email</span>
                  <a href={`mailto:${company.email}`} className="audit-schedule-detail-value">{company.email}</a>
                </div>
                <div>
                  <span className="audit-schedule-detail-label">Sector</span>
                  <span className="audit-schedule-detail-value">{company.sector}</span>
                </div>
                <div>
                  <span className="audit-schedule-detail-label">Phone</span>
                  <span className="audit-schedule-detail-value">{company.phone}</span>
                </div>
                <div>
                  <span className="audit-schedule-detail-label">Company Size</span>
                  <span className="audit-schedule-detail-value">{company.companySize}</span>
                </div>
                <div>
                  <span className="audit-schedule-detail-label">Website</span>
                  <span className="audit-schedule-detail-value">{company.website}</span>
                </div>
                <div>
                  <span className="audit-schedule-detail-label">Address</span>
                  <span className="audit-schedule-detail-value">{company.address}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Segmented Navigation */}
      <nav className="nav nav-segmented nav-3 mb-4" role="tablist">
        <button
          type="button"
          className={`nav-link ${activeTab === 'project-details' ? 'active' : ''}`}
          role="tab"
          aria-selected={activeTab === 'project-details'}
          aria-current={activeTab === 'project-details' ? 'page' : undefined}
          tabIndex={activeTab === 'project-details' ? 0 : -1}
          onClick={() => setActiveTab('project-details')}
        >
          Project Details
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'site-location' ? 'active' : ''}`}
          role="tab"
          aria-selected={activeTab === 'site-location'}
          aria-current={activeTab === 'site-location' ? 'page' : undefined}
          tabIndex={activeTab === 'site-location' ? 0 : -1}
          onClick={() => setActiveTab('site-location')}
        >
          Site Location
        </button>
        <button
          type="button"
          className={`nav-link ${activeTab === 'official-trip' ? 'active' : ''}`}
          role="tab"
          aria-selected={activeTab === 'official-trip'}
          aria-current={activeTab === 'official-trip' ? 'page' : undefined}
          tabIndex={activeTab === 'official-trip' ? 0 : -1}
          onClick={() => setActiveTab('official-trip')}
        >
          Official Trip
        </button>
      </nav>

      {activeTab === 'project-details' && (
      <>
      {/* Audit Details card */}
      <div className="card audit-schedule-section-card mb-4">
        <div className="card-header">
          <h3 className="card-title mb-0">Audit Details</h3>
        </div>
        <div className="card-body">
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <AppSelect
                name="auditType"
                label="Audit Type"
                value={auditType}
                onChange={(name, val) => setAuditType(val || '')}
                options={[
                  { id: 'surveillance-1', name: 'Surveillance - 1' },
                ]}
                placeholder="Select audit type"
                valueField="id"
                labelField="name"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Last Activity Audit Date</label>
              <div className="input-group">
                <input
                  type="date"
                  className="form-control"
                  value={lastActivityDate}
                  onChange={(e) => setLastActivityDate(e.target.value)}
                />
                <span className="input-group-text"><CalendarIcon size={16} /></span>
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label">Latest Audit Date</label>
              <div className="input-group">
                <input
                  type="date"
                  className="form-control"
                  value={latestAuditDate}
                  onChange={(e) => setLatestAuditDate(e.target.value)}
                />
                <span className="input-group-text"><CalendarIcon size={16} /></span>
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label">Total Employee</label>
              <input
                type="number"
                className="form-control"
                value={totalEmployee}
                onChange={(e) => setTotalEmployee(Number(e.target.value) || 0)}
                min={0}
              />
            </div>
          </div>
          <div className="card audit-schedule-info-card mb-3">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-2">
                  <label className="form-label">Mandays</label>
                  <div className="form-control-plaintext">4.0</div>
                </div>
                <div className="col-md-2">
                  <label className="form-label">EA Code</label>
                  <div className="form-control-plaintext">-</div>
                </div>
                <div className="col-md-8">
                  <label className="form-label">Scope</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    readOnly
                    defaultValue="ITSM for the provision of PT Global Solutions Office supporting the provision of Official Agency Email services to Internal Users"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Project accordion */}
      <div className="accordion accordion-flush audit-schedule-section-card mb-4" id="detailProjectAccordion">
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#detailProjectCollapse" aria-expanded="true">
              Detail Project
            </button>
          </h2>
          <div id="detailProjectCollapse" className="accordion-collapse collapse show" data-bs-parent="#detailProjectAccordion">
            <div className="accordion-body">
              <h4 className="mb-3">{surveillanceItem?.name ?? 'Surveillance - 1'}</h4>
              {employees.map((emp) => (
                <div key={emp.id} className="card audit-schedule-employee-card mb-3">
                  <div className="card-header audit-schedule-employee-card-header">
                    <strong className="mb-0">Employee - {employees.findIndex((e) => e.id === emp.id) + 1}</strong>
                    <button type="button" className="btn btn-link text-danger p-0" onClick={() => removeEmployee(emp.id)} aria-label="Remove">
                      <X size={20} />
                    </button>
                  </div>
                  <div className="card-body">
                        <div className="row g-2">
                          <div className="col-6">
                            <label className="form-label">Start Date</label>
                            <div className="input-group">
                              <input type="date" className="form-control" value={emp.startDate} readOnly />
                              <span className="input-group-text"><CalendarIcon size={14} /></span>
                            </div>
                          </div>
                          <div className="col-6">
                            <label className="form-label">End Date</label>
                            <div className="input-group">
                              <input type="date" className="form-control" value={emp.endDate} readOnly />
                              <span className="input-group-text"><CalendarIcon size={14} /></span>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="card audit-schedule-employee-fields-card h-100">
                              <div className="card-body">
                                <div className="row g-2">
                                  <div className="col-12">
                                    <AppSelect
                                      name={`employee-${emp.id}`}
                                      label="Select Employee"
                                      value={emp.employee}
                                      onChange={() => {}}
                                      options={emp.employee ? [{ id: emp.employee, name: emp.employee }] : []}
                                      placeholder="Select employee"
                                      valueField="id"
                                      labelField="name"
                                      disabled
                                    />
                                  </div>
                                  <div className="col-8">
                                    <AppSelect
                                      name={`role-${emp.id}`}
                                      label="Assign Project Roles"
                                      value={emp.role}
                                      onChange={() => {}}
                                      options={emp.role ? [{ id: emp.role, name: emp.role }] : []}
                                      placeholder="Select role"
                                      valueField="id"
                                      labelField="name"
                                      disabled
                                    />
                                  </div>
                                  <div className="col-4">
                                    <label className="form-label">Mandays</label>
                                    <input type="number" className="form-control" value={emp.mandays} readOnly />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="card audit-schedule-employee-fields-card h-100">
                              <div className="card-body">
                                <div className="row g-2">
                                  <div className="col-6">
                                    <AppSelect
                                      name={`auditType-${emp.id}`}
                                      label="Audit Type"
                                      value={emp.auditType}
                                      onChange={() => {}}
                                      options={emp.auditType ? [{ id: emp.auditType, name: emp.auditType }] : []}
                                      placeholder="Select type"
                                      valueField="id"
                                      labelField="name"
                                      disabled
                                    />
                                  </div>
                                  <div className="col-6">
                                    <AppSelect
                                      name={`scheduleStatus-${emp.id}`}
                                      label="Schedule Status"
                                      value={emp.scheduleStatus}
                                      onChange={() => {}}
                                      options={emp.scheduleStatus ? [{ id: emp.scheduleStatus, name: emp.scheduleStatus }] : []}
                                      placeholder="Select status"
                                      valueField="id"
                                      labelField="name"
                                      disabled
                                    />
                                  </div>
                                  <div className="col-12">
                                    <label className="form-label">Select Site</label>
                                    <div className="input-group">
                                      <input type="text" className="form-control" value={emp.site} readOnly />
                                      <button type="button" className="btn btn-outline-secondary" aria-label="Clear">×</button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              <button type="button" className="btn btn-outline-primary" onClick={addEmployee}>
                <PlusIcon className="icon icon-tabler" />
                Add Employee
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Document accordion */}
      <div className="card audit-schedule-section-card mb-4">
        <div className="card-header">
          <h3 className="card-title mb-0">Document</h3>
        </div>
        <div className="card-body p-0">
          <div className="accordion accordion-flush" id="documentAccordion">
            <div className="accordion-item">
              <h2 className="accordion-header">
                <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#documentCollapse" aria-expanded="true">
                  Supporting Documents
                </button>
              </h2>
              <div id="documentCollapse" className="accordion-collapse collapse show" data-bs-parent="#documentAccordion">
                <div className="accordion-body">
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="d-none"
                    accept={ACCEPTED_DOC_TYPES}
                    multiple
                    onChange={handleFileChange}
                  />
                  <div
                    role="button"
                    tabIndex={0}
                    className="audit-schedule-upload-zone mb-3"
                    onClick={handleUploadClick}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleUploadClick(); } }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    aria-label="Upload supporting documents"
                  >
                    <Upload size={32} className="text-muted mb-2" />
                    <p className="mb-0 small text-muted">
                      Click or drag and drop to upload (assignment letter, ticketing, cost breakdown)<br />
                      Supported formats: .pdf, .docx, .jpg
                    </p>
                  </div>
                  <p className="small fw-semibold mb-2">Uploaded Documents</p>
                  <div className="audit-schedule-document-list">
                    {documents.map((doc) => (
                      <div key={doc.id} className="audit-schedule-document-card">
                        <div className="audit-schedule-document-card-icon">
                          <FileText size={20} />
                        </div>
                        <div className="audit-schedule-document-card-info flex-grow-1">
                          <span className="audit-schedule-document-card-name">{doc.name}</span>
                          <span className="audit-schedule-document-card-size">{doc.size}</span>
                        </div>
                        <div className="audit-schedule-document-card-actions d-flex gap-2">
                          <button type="button" className="btn btn-link btn-sm p-0 audit-schedule-document-action audit-schedule-document-action-remove" aria-label="Remove" onClick={() => removeDocument(doc.id)}><X size={18} /></button>
                          <button type="button" className="btn btn-link btn-sm p-0 audit-schedule-document-action" aria-label="View"><Eye size={18} /></button>
                          <button type="button" className="btn btn-link btn-sm p-0 audit-schedule-document-action" aria-label="Download"><Download size={18} /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
      )}

      {activeTab === 'site-location' && (
      <div className="card audit-schedule-site-location-card mb-4">
        <div className="card-body">
          <div className="accordion accordion-flush audit-schedule-site-accordion" id="siteLocationAccordion">
            {sites.map((site, index) => (
              <div key={site.id} className="accordion-item audit-schedule-site-accordion-item">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button audit-schedule-site-accordion-btn ${index > 0 ? 'collapsed' : ''}`}
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#siteCollapse${site.id}`}
                    aria-expanded={index === 0}
                    aria-controls={`siteCollapse${site.id}`}
                  >
                    Site - {site.id}
                  </button>
                </h2>
                <div
                  id={`siteCollapse${site.id}`}
                  className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                >
                  <div className="accordion-body">
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <div className="audit-schedule-site-detail-row">
                          <span className="audit-schedule-site-detail-label">Country</span>
                          <span className="audit-schedule-site-detail-value">{site.country}</span>
                        </div>
                        <div className="audit-schedule-site-detail-row">
                          <span className="audit-schedule-site-detail-label">City</span>
                          <span className="audit-schedule-site-detail-value">{site.city}</span>
                        </div>
                        <div className="audit-schedule-site-detail-row">
                          <span className="audit-schedule-site-detail-label">Site Address</span>
                          <span className="audit-schedule-site-detail-value">{site.siteAddress}</span>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="audit-schedule-site-detail-row">
                          <span className="audit-schedule-site-detail-label">Contact Person</span>
                          <span className="audit-schedule-site-detail-value">{site.contactPerson}</span>
                        </div>
                        <div className="audit-schedule-site-detail-row">
                          <span className="audit-schedule-site-detail-label">Type</span>
                          <span className="audit-schedule-site-detail-value">{site.type}</span>
                        </div>
                        <div className="audit-schedule-site-detail-row">
                          <span className="audit-schedule-site-detail-label">Email</span>
                          <a href={`mailto:${site.email}`} className="audit-schedule-site-detail-value audit-schedule-site-email">{site.email}</a>
                        </div>
                        <div className="audit-schedule-site-detail-row">
                          <span className="audit-schedule-site-detail-label">Phone Number</span>
                          <span className="audit-schedule-site-detail-value">{site.phone}</span>
                        </div>
                      </div>
                    </div>
                    <div className="audit-schedule-site-person-card">
                      <span className="audit-schedule-site-person-avatar">{site.person.initials}</span>
                      <span className="audit-schedule-site-person-name">{site.person.name}</span>
                      <span className="audit-schedule-site-person-mandays">{site.person.mandays} Mandays</span>
                      <span className="audit-schedule-site-person-type">{site.person.auditType}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <div className="accordion-item audit-schedule-site-accordion-item">
              <h2 className="accordion-header">
                <button
                  className="accordion-button audit-schedule-site-accordion-btn collapsed"
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target="#transportAccommodationCollapse"
                  aria-expanded="false"
                  aria-controls="transportAccommodationCollapse"
                >
                  Transportation & Accommodation
                </button>
              </h2>
              <div
                id="transportAccommodationCollapse"
                className="accordion-collapse collapse"
              >
                <div className="accordion-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <p className="audit-schedule-transport-title mb-1">Need Transportation</p>
                      <p className="audit-schedule-transport-desc small text-muted mb-2">Select if the audit execution task requires transportation</p>
                      <div className="d-flex gap-3">
                        <label className="form-check">
                          <input type="radio" name="needTransportation" className="form-check-input" value="yes" checked={needTransportation === 'yes'} onChange={() => setNeedTransportation('yes')} />
                          <span className="form-check-label">Yes</span>
                        </label>
                        <label className="form-check">
                          <input type="radio" name="needTransportation" className="form-check-input" value="no" checked={needTransportation === 'no'} onChange={() => setNeedTransportation('no')} />
                          <span className="form-check-label">No</span>
                        </label>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <p className="audit-schedule-transport-title mb-1">Need Accommodation</p>
                      <p className="audit-schedule-transport-desc small text-muted mb-2">Select if the audit execution task requires accommodation</p>
                      <div className="d-flex gap-3">
                        <label className="form-check">
                          <input type="radio" name="needAccommodation" className="form-check-input" value="yes" checked={needAccommodation === 'yes'} onChange={() => setNeedAccommodation('yes')} />
                          <span className="form-check-label">Yes</span>
                        </label>
                        <label className="form-check">
                          <input type="radio" name="needAccommodation" className="form-check-input" value="no" checked={needAccommodation === 'no'} onChange={() => setNeedAccommodation('no')} />
                          <span className="form-check-label">No</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="audit-schedule-site-location-submit">
            <button type="button" className="btn btn-teal">Submit</button>
          </div>
        </div>
      </div>
      )}

      {activeTab === 'official-trip' && (
      <div className="card audit-schedule-official-trip-card mb-4">
          <input
            ref={officialTripFileInputRef}
            type="file"
            className="d-none"
            accept={ACCEPTED_DOC_TYPES}
            multiple
            onChange={handleOfficialTripFileChange}
          />
          <div className="accordion accordion-flush audit-schedule-official-trip-accordion" id="officialTripAccordion">
            {sitesByPerson.map((person) => {
              const isPersonExpanded = officialTripPersonExpanded[person.personName] !== false;
              return (
                <div key={person.personName} className="accordion-item audit-schedule-official-trip-section audit-schedule-official-trip-person-section">
                  <h2 className="accordion-header">
                    <button
                      type="button"
                      className={`accordion-button audit-schedule-official-trip-section-btn audit-schedule-official-trip-person-btn ${!isPersonExpanded ? 'collapsed' : ''}`}
                      aria-expanded={isPersonExpanded}
                      aria-controls={`officialTripPersonCollapse-${person.personName.replace(/\s+/g, '-')}`}
                      onClick={() => toggleOfficialTripPersonSection(person.personName)}
                    >
                      <span className="audit-schedule-official-trip-section-title">{person.personName}</span>
                      <ChevronDown size={20} className="audit-schedule-official-trip-chevron" />
                    </button>
                  </h2>
                  <div
                    id={`officialTripPersonCollapse-${person.personName.replace(/\s+/g, '-')}`}
                    className={`accordion-collapse collapse ${isPersonExpanded ? 'show' : ''}`}
                  >
                    <div className="accordion-body audit-schedule-official-trip-person-body">
                      <div className="accordion accordion-flush audit-schedule-official-trip-site-accordion" id={`officialTripSiteAccordion-${person.personName.replace(/\s+/g, '-')}`}>
                        {person.sites.map((site) => {
                          const accommodations = officialTripAccommodations[site.id] || [];
                          const isSiteExpanded = officialTripSiteExpanded[site.id] !== false;
                          return (
                            <div key={site.id} className="accordion-item audit-schedule-official-trip-site-section">
                              <h2 className="accordion-header">
                                <button
                                  type="button"
                                  className={`accordion-button audit-schedule-official-trip-section-btn audit-schedule-official-trip-site-btn ${!isSiteExpanded ? 'collapsed' : ''}`}
                                  aria-expanded={isSiteExpanded}
                                  aria-controls={`officialTripSiteCollapse${site.id}`}
                                  onClick={() => toggleOfficialTripSiteSection(site.id)}
                                >
                                  <span className="audit-schedule-official-trip-section-title">Site - {site.id}</span>
                                  <ChevronDown size={20} className="audit-schedule-official-trip-chevron" />
                                </button>
                              </h2>
                              <div
                                id={`officialTripSiteCollapse${site.id}`}
                                className={`accordion-collapse collapse ${isSiteExpanded ? 'show' : ''}`}
                              >
                                <div className="accordion-body">
                                  {accommodations.map((acc) => (
                        <div key={acc.id} className="audit-schedule-official-trip-accommodation-card">
                          <div className="audit-schedule-official-trip-accommodation-header">
                            <h4 className="audit-schedule-official-trip-accommodation-heading">Accommodation</h4>
                            <button
                              type="button"
                              className="audit-schedule-official-trip-accommodation-remove"
                              aria-label="Remove accommodation"
                              onClick={() => removeOfficialTripAccommodation(site.id, acc.id)}
                            >
                              <X size={18} />
                            </button>
                          </div>
                          <div className="row g-3 audit-schedule-official-trip-fields">
                            <div className="col-12">
                              <div className="form-check form-switch audit-schedule-official-trip-toggle">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`client-${site.id}-${acc.id}`}
                                  checked={acc.providedByClient}
                                  onChange={(e) => updateOfficialTripAccommodation(site.id, acc.id, 'providedByClient', e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor={`client-${site.id}-${acc.id}`}>
                                  Provided by Client — Select if the audit execution task requires accommodation
                                </label>
                              </div>
                            </div>
                            <div className="col-12">
                              <AppSelect
                                name={`purpose-${site.id}-${acc.id}`}
                                label="Select Purpose"
                                value={acc.purpose}
                                onChange={(name, val) => updateOfficialTripAccommodation(site.id, acc.id, 'purpose', val || '')}
                                options={OFFICIAL_TRIP_PURPOSES}
                                placeholder="Select purpose"
                                valueField="id"
                                labelField="name"
                              />
                            </div>
                            <div className="col-md-4">
                              <label className="form-label audit-schedule-label-required">Audit Start Date</label>
                              <div className="input-group">
                                <input
                                  type="date"
                                  className="form-control"
                                  value={acc.auditStartDate}
                                  onChange={(e) => updateOfficialTripAccommodation(site.id, acc.id, 'auditStartDate', e.target.value)}
                                />
                                <span className="input-group-text"><CalendarIcon size={16} /></span>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <label className="form-label audit-schedule-label-required">Audit End Date</label>
                              <div className="input-group">
                                <input
                                  type="date"
                                  className="form-control"
                                  value={acc.auditEndDate}
                                  onChange={(e) => updateOfficialTripAccommodation(site.id, acc.id, 'auditEndDate', e.target.value)}
                                />
                                <span className="input-group-text"><CalendarIcon size={16} /></span>
                              </div>
                            </div>
                            <div className="col-md-4">
                              <label className="form-label audit-schedule-label-required">Duration</label>
                              <input
                                type="number"
                                className="form-control"
                                value={acc.duration}
                                onChange={(e) => updateOfficialTripAccommodation(site.id, acc.id, 'duration', Number(e.target.value) || 0)}
                                min={1}
                              />
                            </div>
                            <div className="col-12">
                              <label className="form-label">Amount (refer to CBQA Global Regulation or Else Contract)</label>
                              <textarea
                                className="form-control"
                                rows={3}
                                value={acc.amount}
                                onChange={(e) => updateOfficialTripAccommodation(site.id, acc.id, 'amount', e.target.value)}
                                placeholder="Enter amount or details"
                              />
                            </div>
                            {acc.purpose === 'transport-flight-ticket' && (
                            <div className="col-12">
                              <div className="form-check form-switch audit-schedule-official-trip-toggle">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`pickup-${site.id}-${acc.id}`}
                                  checked={acc.needPickupDropOff}
                                  onChange={(e) => updateOfficialTripAccommodation(site.id, acc.id, 'needPickupDropOff', e.target.checked)}
                                />
                                <label className="form-check-label" htmlFor={`pickup-${site.id}-${acc.id}`}>
                                  Need Pickup/ Drop Off — The need for pick-up and drop-off at the location
                                </label>
                              </div>
                            </div>
                            )}
                            {acc.needPickupDropOff && (
                              <div className="col-12 audit-schedule-official-trip-contacts">
                                <p className="audit-schedule-official-trip-contacts-title">Contact Details</p>
                                {acc.contacts.map((contact) => (
                                  <div key={contact.id} className="audit-schedule-official-trip-contact-row">
                                    <div className="row g-2">
                                      <div className="col-md-6">
                                        <input
                                          type="text"
                                          className="form-control"
                                          placeholder="Full name"
                                          value={contact.name}
                                          onChange={(e) => updateOfficialTripContact(site.id, acc.id, contact.id, 'name', e.target.value)}
                                        />
                                      </div>
                                      <div className="col-md-6">
                                        <input
                                          type="text"
                                          className="form-control"
                                          placeholder="Enter phone number"
                                          value={contact.phone}
                                          onChange={(e) => updateOfficialTripContact(site.id, acc.id, contact.id, 'phone', e.target.value)}
                                        />
                                      </div>
                                      <div className="col-12">
                                        <textarea
                                          className="form-control"
                                          rows={2}
                                          placeholder="Enter Note"
                                          value={contact.note}
                                          onChange={(e) => updateOfficialTripContact(site.id, acc.id, contact.id, 'note', e.target.value)}
                                        />
                                      </div>
                                    </div>
                                    <div className="d-flex gap-2 mt-2">
                                      {acc.contacts.length > 1 && (
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-danger"
                                          onClick={() => removeOfficialTripContact(site.id, acc.id, contact.id)}
                                        >
                                          Remove contact
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary audit-schedule-official-trip-add-contact"
                                  onClick={() => addOfficialTripContact(site.id, acc.id)}
                                >
                                  + Add another contact
                                </button>
                              </div>
                            )}
                            <div className="col-12">
                              <p className="audit-schedule-official-trip-docs-label">Supporting Documents</p>
                              <div
                                role="button"
                                tabIndex={0}
                                className="audit-schedule-upload-zone audit-schedule-official-trip-upload-zone mb-3"
                                onClick={() => openOfficialTripUpload(site.id, acc.id)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openOfficialTripUpload(site.id, acc.id); } }}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  e.currentTarget.classList.remove('audit-schedule-upload-zone--dragover');
                                  const files = e.dataTransfer.files;
                                  if (!files?.length) return;
                                  setOfficialTripUploadTarget({ siteId: site.id, accId: acc.id });
                                  const newDocs = Array.from(files).map((file) => ({
                                    id: Date.now() + Math.random(),
                                    name: file.name,
                                    size: formatFileSize(file.size),
                                    file,
                                  }));
                                  setOfficialTripAccommodations((prev) => ({
                                    ...prev,
                                    [site.id]: (prev[site.id] || []).map((a) =>
                                      a.id === acc.id ? { ...a, documents: [...a.documents, ...newDocs] } : a
                                    ),
                                  }));
                                }}
                                aria-label="Upload supporting documents"
                              >
                                <Upload size={32} className="audit-schedule-official-trip-upload-icon text-muted mb-2" />
                                <p className="audit-schedule-official-trip-upload-text mb-1">Upload supporting documents (assignment letter, ticketing, cost breakdown)</p>
                                <p className="audit-schedule-official-trip-upload-formats small text-muted mb-0">Supported formats: .pdf, .docx, .jpg</p>
                              </div>
                              {acc.documents.length > 0 && (
                                <>
                                  <p className="small fw-semibold mb-2 mt-2">Uploaded Documents</p>
                                  <div className="audit-schedule-document-list audit-schedule-official-trip-document-list">
                                    {acc.documents.map((doc) => (
                                      <div key={doc.id} className="audit-schedule-document-card">
                                        <div className="audit-schedule-document-card-icon">
                                          <FileText size={20} />
                                        </div>
                                        <div className="audit-schedule-document-card-info flex-grow-1">
                                          <span className="audit-schedule-document-card-name">{doc.name}</span>
                                          <span className="audit-schedule-document-card-size">{doc.size}</span>
                                        </div>
                                        <div className="audit-schedule-document-card-actions d-flex gap-2">
                                          <button type="button" className="btn btn-link btn-sm p-0 audit-schedule-document-action" aria-label="View"><Eye size={18} /></button>
                                          <button type="button" className="btn btn-link btn-sm p-0 audit-schedule-document-action" aria-label="Download"><Download size={18} /></button>
                                          <button type="button" className="btn btn-link btn-sm p-0 audit-schedule-document-action audit-schedule-document-action-remove" aria-label="Remove" onClick={() => removeOfficialTripDocument(site.id, acc.id, doc.id)}><X size={18} /></button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                                  ))}
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary audit-schedule-official-trip-add-accommodation"
                                    onClick={() => addOfficialTripAccommodation(site.id)}
                                  >
                                    <PlusIcon size={18} className="me-1" />
                                    Add Accommodation
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
      </div>
      )}

      {(activeTab === 'project-details' || activeTab === 'official-trip') && (
      <div className="audit-schedule-footer">
        <div className="d-flex justify-content-end gap-2">
          <Link to="/calendar" className="btn btn-outline-secondary">Cancel</Link>
          <button type="button" className="btn btn-primary">Add Schedule</button>
        </div>
      </div>
      )}
    </div>
  );
};

export default AuditSchedule;
